import type { RetryPolicy } from './retry-policy';

/**
 * Description of a Modbus function
 * Code in Hexadecimal // comment in decimal
 *
 * @see https://modbus.org/docs/Modbus_Application_Protocol_V1_1b.pdf
 *
 * Modbus data model
 * Discrete Inputs: single bit, read only, this type of data can be provided by an I/O system
 * Coils: single bit, read/write, this type of data can be alterable by an application program
 * Input Registers: 16-bit, read only, this type of data can be provided by an I/O system
 * Holding Registers: 16-bit, read/write, this type of data can be alterable by an application program
 */
export enum ModbusFunctionType {
  ReadDiscreteInputs = 0x02, // 2
  ReadCoils = 0x01, // 1
  WriteSingleCoil = 0x05, // 5
  WriteMultipleCoils = 0x0F, // 15
  ReadInputRegisters = 0x04, // 4
  ReadHoldingRegisters = 0x03, // 3
  WriteSingleRegister = 0x06, // 6
  WriteMultipleRegisters = 0x10, // 16
  ReadWriteMultipleRegisters = 0x17, // 23
  MaskWriteRegister = 0x16, // 22
  ReadFIFOQueue = 0x18, // 24
  CustomV2WriteMultipleRegisters = 0x13, // 19
  ReadFileRecord = 0x14, // 20
  WriteFileRecord = 0x15, // 21
  ReadExceptionStatus = 0x07, // 7
  Diagnostic = 0x08, // 8
  GetComEventCounter = 0x0B, // 11
  GetComEventLog = 0x0C, // 12
  ReportServerID = 0x11, // 17
  ReadDeviceIdentification = 0x2B, // 43
  EncapsulatedInterfaceTransport = 0x2B, // 43
  CANopenGeneralReference = 0x2B, // 43
}

export type MultiRequest = {
  mode: 'read';
  table: Partial<RegisterTable>;
  composer?: Partial<InverterDataComposer>;
  options?: ModbusCommandOps;
  results?: { [key: string]: string };
} | {
  mode: 'write';
  key: keyof RegisterTable;
  value: string | number;
  table: Partial<RegisterTable>;
  options?: ModbusCommandOps;
  results?: { [key: string]: string };
};

export enum CommandPriority {
  High = 1,
  Medium = 2,
  Low = 3,
}

export type ModbusLinkOptions = {
  deviceId?: string,
  inverterVersion?: number,  // 1, 1.5, 2, 3 — undefined = legacy
  concurrentQuery: ConcurrentQuery,
  executeCmd: (cmd: Uint8Array) => Promise<Uint8Array>,
  modbusAddress: number,
  handleFatalError: (error: any) => Promise<void>;
  maxRegisterPerPage?: number,
  maxTimeoutInMs: number,
  delayBetweenCommandsInMs?: number,
  retryPolicy?: RetryPolicy,
}

export interface RequestInfo {
  indexInPromises: number;
  orderIndex: number;
  mode: 'read' | 'write';
  modbusRequest: Uint8Array;
  address?: number;
  quantity?: number;
  table?: Partial<RegisterTable>;
  composer?: Partial<InverterDataComposer>;
  options?: ModbusCommandOps;
}

export interface ModbusCommandOps {
  concurrentQuery?: ConcurrentQuery | null;
  customReadFn?: ModbusFunctionType;
  customWriteFn?: ModbusFunctionType;
  handleFatalError?: (error?: any) => Promise<void>;
  maxRegisterPerPage?: number;
  optimize?: boolean;
  updateProgress?: (percentage: number) => void;
  useValidityMask?: keyof RegisterTable;
  verifyAfterWrite?: boolean;
  verifySkipAddresses?: number[];
  writeRepetitionsCount?: number;
  /** If true, protocol errors (0x01/0x02/0x03) are non-fatal even on legacy inverters.
   *  Use for registers that may not exist on all firmware versions. */
  optional?: boolean;
  /** Expand write range to full aligned blocks of this size (in registers).
   *  Safety registers require complete 64-register block writes — set to 64.
   *  Gap registers are filled from cache (preliminary read). */
  writeBlockSize?: number;
}

export enum ReadWrite {
  Read,
  Write,
  ReadWrite,
}

export enum DataType {
  BCD16,
  BCD_FULL,
  ASCII,
  I16,
  U16,
  I32,
  U32,
  U64,
}

export enum AvoidType {
  SPLIT_PAGE = 1,
  SKIP_REGISTER = 2,
}

// ============ UI Metadata for Register-driven Forms ============

export type RegisterFieldType = 'input' | 'select' | 'checkbox' | 'time' | 'date' | 'weekday' | 'trigger';

export interface RegisterSelectOption {
  value: number | string;
  label: string;
}

export interface RegisterUiMetadata {
  fieldType?: RegisterFieldType;
  inputType?: 'number' | 'text';
  labelKey?: string;
  options?: RegisterSelectOption[];
  displayUnit?: string;
  constraint?: { min?: number; max?: number; step?: number };
  transform?: 'time' | 'date' | 'weekday' | 'multiply' | 'divide' | 'writeMultiply' | 'none';
  transformFactor?: number;
  settingsCode?: string;
  triggerValue?: number;
}

export interface InverterRegister {
  address: number; // Address in HEX
  name: string;
  description: string;
  dataType?: DataType;
  accuracy?: number;
  unit?: string;
  order?: number;
  section?: string[];
  isMask?: boolean;
  limits?: {
    min?: number;
    max?: number;
    unit?: string;
  };
  custom?: {
    value?: InverterContentType;
    translate: string;
    isEnabledFn?: (data: Partial<InverterData>) => boolean;
    isEnabled?: boolean;
    graphOptions?: {
      id: string;
      useOnXAxis?: boolean;
      useOnYAxis?: boolean;
      plotAsAline?: boolean;
      color?: string;
      label?: string;
    };
    bitFunctions?: BitFunction[];
    multiBitFunctions?: MultiBitFunction[];
  };
  access?: ReadWrite;
  quantity: number;
  avoid?: AvoidType;
  ui?: RegisterUiMetadata;
}

export interface BitFunction {
  isEnabled?: boolean;
  isEnabledFn?: (data: Partial<InverterData>) => boolean;
  hide?: boolean;
  section?: string[];
  order?: number;
  translate: string;
  updateFn: (data: Partial<InverterData>, bit: boolean) => number;
}

export interface MultiBitFunction {
  selection?: number;
  selectionFn?: (data: Partial<InverterData>) => number;
  selectionLabelMap?: Record<number, string>;
  hide?: boolean;
  section?: string[];
  order?: number;
  translate: string;
  updateFn: (data: Partial<InverterData>, selection: number) => number;
}

export interface RegisterPage {
  address: number;
  quantity: number;
}

export type RegisterTable = Record<string, InverterRegister>;

export type ErrorFeature = {
  description: string;
  source: string;
  classification: string;
  warning?: string;
  id?: number;
  level?: number;
};

export enum CustomInverterType {
  ArrayObject = 'array-object',
  Object = 'object',
  EventList = 'event-list',
}

export type CustomInverterResult = {
  type: CustomInverterType.ArrayObject;
  value: object[]
} | {
  type: CustomInverterType.Object;
  value: object;
} | {
  type: CustomInverterType.EventList;
  value: {
    descriptions: ErrorFeature[];
    date: Date;
    id: number;
    index : number;
  };
};

export type ConcurrentQuery = {
  count: number;
  encodeMultiQuery: EncodeMultiQueryFn;
  decodeMultiQuery: DecodeMultiQueryFn;
};

export type EncodeMultiQueryFn = (
  requests: Uint8Array[],
) => Uint8Array;

export type DecodeMultiQueryFn = (
  multiResponse: Uint8Array,
  numberOfSubRequests: number,
) => Uint8Array[];

export type InverterContentType = number | string | null | BigInt | CustomInverterResult;
export type InverterData = Record<string, InverterContentType>;

export type InverterDataComposer = Record<string, (inverterData: Partial<InverterData>) => Partial<InverterData>>;

export interface FirmwareInfo {
  serial: string;
  modules: {
      ARM: string;
      DSPM: string;
      DSPS: string;
      SAFETY: string;
      SOLARMAN_LOGGER: string;
  };
}
